import { ActionType, PayloadAction } from 'typesafe-actions';
import {
  CRField,
  CROption,
  FieldDefnOverride,
  PatchData,
} from '@smart-builder/types';
import { APIParams, ApiRequestRange } from '../../callApi';

export enum ApiActionType {
  cancel,
  clear,
  delete,
  deleteNested,
  insert,
  load,
  loadMulti,
  register,
  save,
  unregister,
  update,
}

export type SaveChange<TItemBody> = {
  onSuccess?: OnSuccessHandler<TItemBody>;
  onFailure?: (error: any) => void;
  reloadSnapshots?: boolean;
};

export type SimpleChange<TItemBody> = {
  onSuccess?: OnSuccessHandler<TItemBody>;
  onFailure?: (error: any) => void;
};

export type FieldChange<TItemBody> = SimpleChange<TItemBody> & {
  fieldName: keyof TItemBody;
  value?: any;
  promptAnswers?: any; // any answers as a result of prompting the user
  sequenceNum?: number; // sequence num of the attribute, not the body // TODO SM Rename
};

export type ItemBodyParser<TItemBody, TItemBodyAPI> = (
  itemBody: TItemBodyAPI,
) => TItemBody;

// merge newly fetched body with existing body, after a load or update
export type ItemBodyMerger<TItemBody> = (
  lastBody: TItemBody,
  currentBody: TItemBody,
  isUpdate: boolean,
  lastSeqNum: number,
  curSeqNum: number,
) => TItemBody;

// combines old summary + current body to produce current summary
// i.e. builds a summary by using the body values, used to update
//    the grid when changing the body details
export type ItemSummaryBodyMerger<TItemSummary, TItemBody> = (
  oldSummary?: TItemSummary,
  curBody?: TItemBody,
) => TItemSummary;

export type EndPointBuilder<TItemKey, TItemSummary, TItemBody> = (
  key?: TItemKey | TItemSummary | TItemBody,
) => string;

export type FieldChangeFormatter<TItemBody> = (
  fieldName: keyof TItemBody,
  value: any,
) => any;

export type QueryParamSelector<TItemBody> = (body: TItemBody) => any;

// returns whether a & b both have the same values for key attributes
// must return true if neither args have keys (including if one or both are undefined)
export type KeyComparer<TItemKey, TItemSummary, TItemBody> = (
  a: TItemKey | TItemSummary | TItemBody | undefined,
  b: TItemKey | TItemSummary | TItemBody | undefined,
) => boolean;

export enum ValidationSeverity {
  info,
  warning,
  error,
}
export type ValidationEntry = {
  severity: ValidationSeverity;
  message: string;
};
export type ValidationRecord<TItemBody> = Partial<
  Record<keyof TItemBody, ValidationEntry>
>;
export type ValidationResult<TItemBody> = {
  warningCount: number;
  errorCount: number;
  byField: ValidationRecord<TItemBody>;
};

export type ResourceValidator<TItemBody> = (
  body?: Partial<TItemBody>,
) => ValidationRecord<TItemBody>;

// TODO SM Deprecate
export type BaseResourceState<TItemBody> = {
  pendingLoads: number; // number of load requests that are currently pending
  pendingUpdates: number; // number of save/delete requests that are currently pending
  error?: any; // last received error

  //? AY maybe a currentSummary would be convenient to have in derivedState
  // should simply be currentSummary = currentItems[currentRecordIndex]?
  currentBody?: TItemBody;
};

export type EmptyPayload = Record<string, never>;
export type SimpleUpdateActionCreator<TItemBody> = (
  onSuccess?: OnSuccessHandler<TItemBody>,
  onFailure?: () => void,
) => PayloadAction<string, SimpleChange<TItemBody>>;
export type SaveActionCreator<TItemBody> = (
  onSuccess?: OnSuccessHandler<TItemBody>,
  onFailure?: () => void,
  reloadSnapshots?: boolean,
) => PayloadAction<string, SaveChange<TItemBody>>;
export type KeyOnlyPayload<TItemKey> = {
  key: TItemKey;
  onSuccess?: () => void;
  onFailure?: () => void;
};
export type InsertPayload<TItemBody, TItemKey> = {
  body?: Partial<TItemBody>;
  data?: any;
  onSuccess?: OnSuccessHandler<TItemBody>;
  onFailure?: () => void;
  bodyFragment?: RequestBodyFragment;
};
export type KeyOnlyActionCreator<TItemKey> = (
  key: TItemKey,
  onSuccess?: () => void,
  onFailure?: () => void,
) => PayloadAction<string, KeyOnlyPayload<TItemKey>>;
export type LoadMultiPayload = APIParams;
export type LoadSinglePayload<TItemKey> = { key: TItemKey; params?: APIParams };
export type SelectPayload<TItemKey> = { key?: TItemKey };
export type LoadSingleActionCreator<TItemKey, TLoadSingleQueryParams> = (
  key: TItemKey,
  params?: TLoadSingleQueryParams,
) => PayloadAction<string, LoadSinglePayload<TItemKey>>;
export type LoadMultiActionCreator<TLoadMultiQueryParams> = (
  params?: TLoadMultiQueryParams,
  range?: ApiRequestRange,
) => PayloadAction<string, LoadMultiPayload>;
export type BodyStatePayload = { bodyState: BodyState };
export type BodyStateActionCreator = (
  bodyState: BodyState,
) => PayloadAction<string, BodyStatePayload>;

export enum BodyState {
  browse,
  edit,
  insert,
}

export type PatchOp =
  | 'update'
  | 'add'
  | 'remove'
  | 'save'
  | 'cancel'
  | 'initialise';

export type FieldChangeEntry = FieldChange<any> & {
  op: PatchOp;
  bodySeqNum: number;
};

export type RequestBodyFragmentValue = {
  fields?: string[];
  fieldsRO?: string[];
  answers?: { [questionName: string]: any };
  dataPacket?: { [name: string]: any };
  nested?: Record<string, RequestBodyFragmentValue>;
};

export type RequestBodyFragment = {
  [key: string]: RequestBodyFragmentValue;
};

export type FieldDefnOverrideFragment = FieldDefnOverride | FieldDefnOverride[];

type MainResourceRecord<TItemKey, TItemSummary, TItemBody> = {
  bodyState: BodyState;
  summary: TItemSummary;
  body?: TItemBody;

  loadedLockNum?: number;
  seqNumFrom?: number; // corresponds to the change before any pending changes are applied
  seqNumCur?: number; // corresponds to the change after all pending changes have been applied

  pendingChanges: FieldChangeEntry[];
  validation?: ValidationResult<TItemBody>;
};

export type MainResourceState<TItemKey, TItemSummary, TItemBody> = {
  // if a request was made for a particular frame sequence number and
  //    when the response is received the sequence number is higher,
  //    the response is ignored
  frameSeqNumber: number;
  frameMultiSeqNumber: number;

  pendingLoads: number; // number of load requests that are currently pending
  pendingUpdates: number; // number of save/delete requests that are currently pending

  // last received error/response
  patchData?: PatchData;
  error?: any;

  records: MainResourceRecord<TItemKey, TItemSummary, TItemBody>[];

  // if this is a nested dataset, insertedKeys and deletedKeys can be arbitrary size.
  // otherwise, insertedKeys will be at most size 1, deletedKeys will always be empty.
  insertedKeys: TItemKey[];
  deletedKeys: TItemKey[];

  currentRecordIndex?: number;
  currentRecord?: MainResourceRecord<TItemKey, TItemSummary, TItemBody>;
  currentBody?: TItemBody; // calculated
  currentItems: TItemSummary[]; // calculated

  lastLoadParams?: LoadPayload<TItemKey, TItemBody>;
  lastLoadMultiParams?: LoadMultiPayload;
  requestBodyFragments: RequestBodyFragment[];
  overrides: (FieldDefnOverride | FieldDefnOverride[])[];
  populateOptions?: Record<string, CROption[]>;

  lastRecordsChangedAction?: ApiActionType; // will be set whenever records are changed
  cancelCount?: number;

  hasLoadMulti?: boolean;
};

export type MainResourceHandler<TItemKey, TItemSummary, TItemBody, TAction> = (
  state: MainResourceState<TItemKey, TItemSummary, TItemBody>,
  action: ActionType<TAction>,
) => MainResourceState<TItemKey, TItemSummary, TItemBody>;

export type MainStateSelector<TItemKey, TItemSummary, TItemBody> = (
  state: any,
) => MainResourceState<TItemKey, TItemSummary, TItemBody>;

export type OnSuccessHandler<TItemBody> = (
  body?: TItemBody,
  patchData?: PatchData,
) => void;

export type ResourceListener = (
  requestBodyFragment: RequestBodyFragment | '' | undefined,
  overrides: FieldDefnOverrideFragment | undefined, // Special value.
  deps: any[],
  doListen?: boolean,
) => void;

export type InsertActionCreator<TItemBody, TItemKey> = (
  body?: Partial<TItemBody>,
  data?: any,
  onSuccess?: OnSuccessHandler<TItemBody>,
  onFailure?: () => void,
  bodyFragment?: RequestBodyFragment,
) => PayloadAction<string, InsertPayload<TItemBody, TItemKey>>;

export type LoadPayload<TItemKey, TItemBody> = {
  key: TItemKey;
  queryParams?: any;
  bodyFragment?: RequestBodyFragment;
  overrides?: FieldDefnOverride;
  allowMerge?: true | undefined; // Should always be undefined when calling load, as to force a fresh load
  onSuccess?: OnSuccessHandler<TItemBody>;
  onFailure?: () => void;
};
export type LoadActionCreator<TItemKey, TItemBody> = (
  key: TItemKey,
  queryParams?: any,
  bodyFragment?: RequestBodyFragment,
  overrides?: FieldDefnOverride,
) => PayloadAction<string, LoadPayload<TItemKey, TItemBody>>;

export type CustomUpdateValue = { clientValue: any; serverUpdateValue: any };

export type UpdateActionCreator<TItemBody> = (
  fieldName: string,
  value: any | CustomUpdateValue,
  op?: PatchOp,
  onSuccess?: OnSuccessHandler<TItemBody>,
  onFailure?: () => void,
) => PayloadAction<string, FieldChange<any>>;

export type RegisterPayload = {
  fragment: RequestBodyFragment;
  overrides?: FieldDefnOverrideFragment;
  requireLoad?: boolean;
};
export type RegisterActionCreator = (
  fragment: RequestBodyFragment,
  overrides?: FieldDefnOverrideFragment,
  requireLoad?: boolean,
) => PayloadAction<string, RegisterPayload>;

export type SelectRecordActionCreator<TItemKey> = (
  key?: TItemKey,
  onSuccess?: () => void,
) => PayloadAction<string, SelectPayload<TItemKey>>;

export type EmptyPayloadActionCreator = () => PayloadAction<
  string,
  EmptyPayload
>;

export type MainResourceActionCreators<
  TItemKey,
  TItemSummary,
  TItemBody,
  TLoadMultiQueryParams,
> = {
  register: RegisterActionCreator;
  unregister: RegisterActionCreator;
  selectRecord: SelectRecordActionCreator<TItemKey>;
  clear: EmptyPayloadActionCreator;
  clearErrorAndPatchData: EmptyPayloadActionCreator;

  cancel: SimpleUpdateActionCreator<TItemBody>;
  save: SaveActionCreator<TItemBody>;
  load: LoadActionCreator<TItemKey, TItemBody>;
  loadMulti: LoadMultiActionCreator<TLoadMultiQueryParams>;

  insert: InsertActionCreator<TItemBody, TItemKey>;
  update: UpdateActionCreator<TItemBody>;
  delete: KeyOnlyActionCreator<TItemKey>;
  deleteNested: KeyOnlyActionCreator<TItemKey>;

  setBodyState: BodyStateActionCreator;
};

export type ItemSummaryParser<TItemSummary, TItemSummaryAPI> = (
  item: TItemSummaryAPI,
) => TItemSummary;

/** returns the keys of T that are CRFields (including the optional ones) */
// things to be wary of if changing this type
// this needs to include optional properties of `T`
// obviously, should not include fields of type CRImageFullKey and anything that is not a CRField
// the resulting union should NOT contain `undefined`
// this is only necessary due to not all fields being CRField<T>
export type CRFieldKeys<T> = keyof Pick<
  T,
  {
    [K in keyof T]-?: T[K] extends CRField | undefined ? K : never;
  }[keyof T]
>;

export type AnswerOf<T> = T extends CRField<infer FieldType> | undefined
  ? FieldType
  : never;

export type UnwrappedCRField<T> = T extends CRField<infer FieldType> | undefined
  ? FieldType
  : T;

// when patching, all fields that were received as CRFields are sent as simple values (i.e. their answers),
//  hence the mapped type below.
export type PatchBodyOf<TItemBody> = Partial<{
  [key in keyof TItemBody]: TItemBody[key] extends
    | CRField<infer FieldType>
    | undefined
    ? FieldType
    : TItemBody[key];
}>;

export type HTTPMethod = 'PATCH' | 'GET' | 'PUT' | 'POST' | 'DELETE';

export type ItemPair<TItemSummary, TItemBody> = {
  summary: TItemSummary;
  body: TItemBody;
};
